import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { ROUTES } from '@/app/routes';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations, useCurrentUser } from '@/hooks';
import { buildFacilityOptions } from '@/config/formOptions';
import { EMPTY_LIST } from '@/lib/utils';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import {
  FulfillForm,
  FulfillSuccessModal,
  FacilityField,
  RetireCreditsField,
  VesselNameField,
  buildBatchesFromValues,
  FULFILL_UNIT_TYPE_BASIS,
} from '@/components/common';
import { ShipmentTerminalField } from '../components/ShipmentTerminalField';
import {
  useGetShipmentForFulfillQuery,
  useFulfillOutgoingShipmentMutation,
} from '../api/outgoingShipmentsApi';
import { FULFILL_SHIPMENT_CONFIG } from '../constants/fulfillShipmentConfig';

// Outbounds ▸ Outgoing shipments ▸ Fulfill request.
export const FulfillShipmentPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();
  const user = useCurrentUser();
  const [isSuccessOpen, setSuccessOpen] = useState(false);
  // Filled by the fulfil's response; Generate declaration opens these.
  const [createdAllocationIds, setCreatedAllocationIds] = useState([]);

  const { data: shipment, isLoading, isError } = useGetShipmentForFulfillQuery({ id: shipmentId });
  const [fulfillShipment, { isLoading: isFulfilling }] = useFulfillOutgoingShipmentMutation();

  const goToShipment = useCallback(() => {
    navigate(ROUTES.outgoingShipmentDetail(module, shipmentId));
  }, [navigate, module, shipmentId]);

  const goToShipments = useCallback(() => {
    navigate(ROUTES.outgoingShipments(module));
  }, [navigate, module]);

  const goToGenerateDeclaration = useCallback(() => {
    navigate(ROUTES.generateDeclarationFor(module, createdAllocationIds));
  }, [navigate, module, createdAllocationIds]);

  const handleSubmit = useCallback(
    async (values) => {
      // AllocateCreditSection already shows the mixed-units Alert inline.
      if (values.hasMixedUnitBatches) return;
      // The picks and their typed amounts are form fields; the payload wants them as one list.
      const batches = buildBatchesFromValues(values);
      // The basis the picks were read in — the assigned quantity travels in that unit's symbol.
      const assignedQuantityUnit = rowUnitSymbol(
        shipment,
        FULFILL_UNIT_TYPE_BASIS[values.fulfillUnitType],
      );
      try {
        const allocationIds = await fulfillShipment({
          module,
          id: shipmentId,
          values: { ...values, batches, assignedQuantityUnit },
        }).unwrap();
        setCreatedAllocationIds(allocationIds);
        setSuccessOpen(true);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [fulfillShipment, module, shipmentId, shipment],
  );

  const facilityOptions = useMemo(
    () =>
      buildFacilityOptions({
        sites: user?.sites ?? EMPTY_LIST,
        backToBackAccounts: user?.backToBackAccounts ?? EMPTY_LIST,
      }),
    [user],
  );

  // <Form> seeds initialValues once, on mount, so it waits for the shipment.
  const initialValues = useMemo(() => shipment?.values, [shipment]);

  const sites = user?.sites ?? EMPTY_LIST;

  // The fields each section shows; the config says which sections exist and when.
  const slots = useMemo(
    () => ({
      allocations: (
        <>
          <FacilityField options={facilityOptions} label={t('FIELD_FACILITY_NAME')} disabled />
          <ShipmentTerminalField sites={sites} disabled={!shipment?.hasTerminal} />
          <VesselNameField />
          <RetireCreditsField />
        </>
      ),
    }),
    [t, facilityOptions, sites, shipment?.hasTerminal],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !shipment) {
    return (
      <EmptyState
        title={t('OUTGOING_SHIPMENTS_FULFILL_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToShipments}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <FulfillForm
        config={FULFILL_SHIPMENT_CONFIG}
        module={module}
        slots={slots}
        record={shipment}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={goToShipment}
        isSubmitting={isFulfilling}
      />

      <FulfillSuccessModal
        open={isSuccessOpen}
        onBackToOutbounds={goToShipments}
        onGenerateDeclaration={goToGenerateDeclaration}
      />
    </>
  );
};
